import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/lib/property";
import { useIsMaster } from "@/lib/auth";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { RES } from "@/constants/testIds";
import GuestPicker from "@/components/GuestPicker";
import { GuestContactEdit } from "@/components/GuestContactEdit";
import { StayActions } from "@/components/StayActions";
import { PlusIcon, SignInIcon, SignOutIcon, XCircleIcon, ReceiptIcon, UsersThreeIcon, XIcon, TrashIcon, StackIcon } from "@phosphor-icons/react";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

function statusBadge(s) {
  const map = {
    booked: "bg-primary/10 text-primary",
    checked_in: "bg-secondary/15 text-secondary",
    checked_out: "bg-muted text-muted-foreground",
    cancelled: "bg-destructive/10 text-destructive",
  };
  return <Badge variant="outline" className={map[s]}>{s.replace("_", " ")}</Badge>;
}

export default function Reservations() {
  const { activeId } = useProperty();
  const isMaster = useIsMaster();
  const [items, setItems] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [guests, setGuests] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    guest_id: "", room_id: "",
    check_in: new Date().toISOString().slice(0, 10),
    check_out: new Date(Date.now() + 86400000).toISOString().slice(0, 10),
    adults: 1, children: 0, notes: "", rate_override: "",
    deposit_amount: "", deposit_mode: "cash", deposit_reference: "",
    additional_guest_ids: [],
    plan_code: "EP", extra_bed_count: 0, cnb_count: 0,
    early_checkin_no_charge: false, late_checkout_no_charge: false,
  });
  const [statusFilter, setStatusFilter] = useState("all");
  const [platform, setPlatform] = useState({ meal_plans: [] });

  const load = () => {
    if (!activeId) return;
    api.get("/reservations", { params: { property_id: activeId } }).then((r) => setItems(r.data));
    api.get("/rooms", { params: { property_id: activeId } }).then((r) => setRooms(r.data));
    api.get("/guests").then((r) => setGuests(r.data));
  };
  useEffect(() => { load(); }, [activeId]);
  useEffect(() => {
    api.get("/settings/platform/public").then((r) => {
      // public doesn't include meal_plans — fetch full for admins
    }).catch(() => {});
    api.get("/settings/platform").then((r) => setPlatform(r.data || {})).catch(() => {});
  }, []);

  const selectedRoom = rooms.find((r) => r.id === form.room_id);
  const baseRate = selectedRoom?.rate_per_night || 0;

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        property_id: activeId,
        adults: Number(form.adults),
        children: Number(form.children),
      };
      // Only send rate_override when user typed a positive number
      const rov = Number(form.rate_override);
      if (form.rate_override !== "" && !isNaN(rov) && rov > 0) {
        payload.rate_override = rov;
      } else {
        delete payload.rate_override;
      }
      // Deposit — only send when > 0
      const dep = Number(form.deposit_amount);
      if (form.deposit_amount !== "" && !isNaN(dep) && dep > 0) {
        payload.deposit_amount = dep;
        payload.deposit_mode = form.deposit_mode || "cash";
        if (form.deposit_reference) payload.deposit_reference = form.deposit_reference;
      } else {
        delete payload.deposit_amount;
        delete payload.deposit_mode;
        delete payload.deposit_reference;
      }
      await api.post("/reservations", payload);
      toast.success(`Reservation created${payload.additional_guest_ids?.length ? ` · ${payload.additional_guest_ids.length + 1} guests` : ""}`);
      setOpen(false);
      setForm((f) => ({ ...f, rate_override: "", deposit_amount: "", deposit_reference: "", additional_guest_ids: [] }));
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to book"); }
  };

  const patch = async (id, updates) => {
    try {
      await api.patch(`/reservations/${id}`, updates);
      if (updates.status === "checked_in") toast.success("Guest checked in");
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Update failed";
      toast.error(msg);
    }
  };

  const cancelCheckin = async (r) => {
    if (!window.confirm(`Undo check-in for ${r.guest?.name}? Room ${r.room?.number} will revert to available.`)) return;
    try {
      await api.post(`/reservations/${r.id}/cancel-checkin`);
      toast.success("Check-in reversed");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const cancelCheckout = async (r) => {
    if (!window.confirm(`Undo check-out for ${r.guest?.name}? The draft invoice (if any) will be deleted and the room re-marked occupied.`)) return;
    try {
      await api.post(`/reservations/${r.id}/cancel-checkout`);
      toast.success("Check-out reversed");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const checkoutAndBill = async (r) => {
    if (!window.confirm(`Check-out ${r.guest?.name} from Room ${r.room?.number} and generate the invoice?`)) return;
    try {
      const res = await api.post(`/reservations/${r.id}/checkout`, {});
      if (res.data.generated) {
        toast.success(`Checked out · Invoice ${res.data.invoice?.number} generated (${inr(res.data.invoice?.total)})`);
      } else if (res.data.already_checked_out) {
        toast.info("Already checked out");
      } else {
        toast.success(`Checked out · Invoice ${res.data.invoice?.number} already exists`);
      }
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Check-out failed"); }
  };

  const removeReservation = async (r) => {
    const label = `${r.guest?.name || "Guest"} · Room ${r.room?.number || ""} · ${r.check_in}`;
    if (!window.confirm(`Delete reservation for ${label}? This cannot be undone. Any linked invoice will also be removed.`)) return;
    try {
      await api.delete(`/reservations/${r.id}`);
      toast.success("Reservation deleted");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const addAdditionalGuest = (gid) => {
    if (!gid || gid === form.guest_id || form.additional_guest_ids.includes(gid)) return;
    setForm({ ...form, additional_guest_ids: [...form.additional_guest_ids, gid] });
  };
  const removeAdditionalGuest = (gid) => setForm({ ...form, additional_guest_ids: form.additional_guest_ids.filter((x) => x !== gid) });

  const [q, setQ] = useState("");
  const [roomFilter, setRoomFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState(""); // ISO yyyy-mm-dd — matches any reservation active on that day
  const filtered = useMemo(() => {
    let base = statusFilter === "all" ? items : items.filter((i) => i.status === statusFilter);
    if (roomFilter !== "all") base = base.filter((r) => r.room_id === roomFilter);
    if (dateFilter) base = base.filter((r) => r.check_in <= dateFilter && r.check_out >= dateFilter);
    const term = q.trim().toLowerCase();
    if (!term) return base;
    return base.filter((r) =>
      (r.booking_ref || "").toLowerCase().includes(term) ||
      (r.guest?.name || "").toLowerCase().includes(term) ||
      (r.guest?.phone || "").toLowerCase().includes(term) ||
      (r.room?.number || "").toLowerCase().includes(term)
    );
  }, [items, statusFilter, roomFilter, dateFilter, q]);
  const clearFilters = () => { setQ(""); setStatusFilter("all"); setRoomFilter("all"); setDateFilter(""); };
  const hasActiveFilter = q || statusFilter !== "all" || roomFilter !== "all" || dateFilter;

  return (
    <div>
      <PageHeader
        overline="Front Desk"
        title="Reservations"
        description="Bookings, check-ins and check-outs for the selected property."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              placeholder="Search booking / guest / phone / room…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-56"
              data-testid="reservation-search"
            />
            <Select value={roomFilter} onValueChange={setRoomFilter}>
              <SelectTrigger className="w-32" data-testid="reservation-room-filter"><SelectValue placeholder="Room" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All rooms</SelectItem>
                {[...rooms].sort((a, b) => String(a.number).localeCompare(String(b.number), undefined, { numeric: true })).map((r) => (
                  <SelectItem key={r.id} value={r.id} data-testid={`reservation-room-opt-${r.number}`}>#{r.number} · {r.room_type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex flex-col">
              <Input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-40"
                data-testid="reservation-date-filter"
                title="Show reservations active on this date"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36" data-testid="reservation-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="booked">Booked</SelectItem>
                <SelectItem value="checked_in">Checked In</SelectItem>
                <SelectItem value="checked_out">Checked Out</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            {hasActiveFilter && (
              <Button type="button" variant="ghost" size="sm" onClick={clearFilters} data-testid="reservation-clear-filters"><XIcon size={12} className="mr-1" />Clear</Button>
            )}
            <span className="text-xs text-muted-foreground" data-testid="reservation-result-count">{filtered.length} of {items.length}</span>
            <BulkBookDialog rooms={rooms} guests={guests} activeId={activeId} onCreated={() => api.get("/reservations", { params: { property_id: activeId } }).then((r) => setItems(r.data))} onGuestsRefresh={() => api.get("/guests").then((r) => setGuests(r.data))} />
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button data-testid={RES.newBtn}><PlusIcon size={16} className="mr-1" />New booking</Button></DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>New reservation</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                  <div>
                    <Label>Guest</Label>
                    <GuestPicker
                      guests={guests}
                      value={form.guest_id}
                      onChange={(id) => setForm({ ...form, guest_id: id })}
                      onGuestCreated={() => api.get("/guests").then((r) => setGuests(r.data))}
                      testIdPrefix="reservation-guest"
                    />
                  </div>
                  <div>
                    <Label>Room</Label>
                    <Select value={form.room_id} onValueChange={(v) => setForm({ ...form, room_id: v })}>
                      <SelectTrigger data-testid={RES.roomSelect}><SelectValue placeholder="Choose room" /></SelectTrigger>
                      <SelectContent>
                        {rooms.filter((r) => r.status === "available").map((r) => (
                          <SelectItem key={r.id} value={r.id}>{r.number} · {r.room_type} · ₹{r.rate_per_night}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Check-in</Label><Input type="date" min={new Date().toISOString().slice(0,10)} value={form.check_in} onChange={(e) => setForm({ ...form, check_in: e.target.value })} data-testid={RES.checkInDate} required /></div>
                    <div><Label>Check-out</Label><Input type="date" min={form.check_in || new Date().toISOString().slice(0,10)} value={form.check_out} onChange={(e) => setForm({ ...form, check_out: e.target.value })} data-testid={RES.checkOutDate} required /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Adults</Label><Input type="number" min="1" value={form.adults} onChange={(e) => setForm({ ...form, adults: e.target.value })} /></div>
                    <div><Label>Children</Label><Input type="number" min="0" value={form.children} onChange={(e) => setForm({ ...form, children: e.target.value })} /></div>
                  </div>

                  {/* Meal plan + extras */}
                  <div className="rounded-md border border-border p-3 space-y-3" data-testid="reservation-plan-extras">
                    <div>
                      <Label className="text-sm">Meal plan</Label>
                      <Select value={form.plan_code} onValueChange={(v) => setForm({ ...form, plan_code: v })}>
                        <SelectTrigger className="mt-1" data-testid="reservation-plan-code"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {(platform.meal_plans || []).map((p) => (
                            <SelectItem key={p.code} value={p.code}>{p.code} — {p.label}{p.surcharge_per_person > 0 ? ` (+₹${p.surcharge_per_person}/pax/night)` : ""}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Extra beds <span className="text-muted-foreground">(₹{platform.extra_bed_rate || 500}/night each)</span></Label>
                        <Input type="number" min="0" value={form.extra_bed_count} onChange={(e) => setForm({ ...form, extra_bed_count: Number(e.target.value) })} data-testid="reservation-extra-bed" />
                      </div>
                      <div>
                        <Label className="text-xs">CNB (child no bed) <span className="text-muted-foreground">(₹{platform.cnb_rate || 300}/night each)</span></Label>
                        <Input type="number" min="0" value={form.cnb_count} onChange={(e) => setForm({ ...form, cnb_count: Number(e.target.value) })} data-testid="reservation-cnb" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={form.early_checkin_no_charge} onChange={(e) => setForm({ ...form, early_checkin_no_charge: e.target.checked })} data-testid="reservation-early-free" className="accent-primary" />
                        Complimentary early check-in
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={form.late_checkout_no_charge} onChange={(e) => setForm({ ...form, late_checkout_no_charge: e.target.checked })} data-testid="reservation-late-free" className="accent-primary" />
                        Complimentary late check-out
                      </label>
                    </div>
                  </div>
                  <div className="rounded-md border border-border p-3">
                    <Label className="text-sm flex items-center gap-1"><UsersThreeIcon size={12} weight="duotone" /> Additional guests sharing this room</Label>
                    <p className="text-[10px] text-muted-foreground mt-1">All guests need KYC (photo + ID card) before check-in. Primary guest is billed.</p>
                    <div className="mt-2 grid grid-cols-[1fr_auto] gap-2 items-start">
                      <Select value="" onValueChange={addAdditionalGuest}>
                        <SelectTrigger data-testid="reservation-additional-guest-picker"><SelectValue placeholder="Pick an existing guest to add…" /></SelectTrigger>
                        <SelectContent>
                          {guests
                            .filter((g) => g.id !== form.guest_id && !form.additional_guest_ids.includes(g.id))
                            .slice(0, 200)
                            .map((g) => (
                              <SelectItem key={g.id} value={g.id}>{g.name}{g.phone ? ` · ${g.phone}` : ""}</SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {form.additional_guest_ids.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2" data-testid="reservation-additional-guest-list">
                        {form.additional_guest_ids.map((gid) => {
                          const g = guests.find((x) => x.id === gid);
                          return (
                            <span key={gid} className="inline-flex items-center gap-1 text-xs rounded-full bg-primary/10 border border-primary/30 text-primary px-2 py-0.5" data-testid={`reservation-addl-chip-${gid}`}>
                              {g?.name || "Guest"}
                              <button type="button" onClick={() => removeAdditionalGuest(gid)} className="hover:text-destructive"><XIcon size={10} /></button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="rounded-md border border-border p-3">
                    <div className="flex items-baseline justify-between">
                      <Label className="text-sm">Rate per night (₹) — one-time override</Label>
                      {baseRate > 0 && (
                        <span className="text-[10px] text-muted-foreground font-mono-plex">
                          Base ₹{Number(baseRate).toLocaleString("en-IN")}
                        </span>
                      )}
                    </div>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.rate_override}
                      onChange={(e) => setForm({ ...form, rate_override: e.target.value })}
                      placeholder={baseRate ? `Leave blank to use base rate (${baseRate})` : "Select a room first"}
                      data-testid="reservation-rate-override"
                      className="mt-1"
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Applies only to this booking · does not touch OTA rates or the rate calendar.
                    </p>
                  </div>
                  <div className="rounded-md border border-border p-3">
                    <Label className="text-sm">Advance / Deposit at booking (₹)</Label>
                    <div className="grid grid-cols-3 gap-2 mt-1">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={form.deposit_amount}
                        onChange={(e) => setForm({ ...form, deposit_amount: e.target.value })}
                        data-testid="reservation-deposit-amount"
                      />
                      <Select value={form.deposit_mode} onValueChange={(v) => setForm({ ...form, deposit_mode: v })}>
                        <SelectTrigger data-testid="reservation-deposit-mode"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cash">Cash</SelectItem>
                          <SelectItem value="upi">UPI</SelectItem>
                          <SelectItem value="card">Card</SelectItem>
                          <SelectItem value="bank">Bank transfer</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        placeholder="Ref / UTR"
                        value={form.deposit_reference}
                        onChange={(e) => setForm({ ...form, deposit_reference: e.target.value })}
                        data-testid="reservation-deposit-ref"
                      />
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Automatically subtracted from the balance at check-out billing.
                    </p>
                  </div>
                  <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                  <DialogFooter><Button type="submit" data-testid={RES.submitBtn}>Book</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Booking</TableHead>
              <TableHead>Guest</TableHead>
              <TableHead>Room</TableHead>
              <TableHead>Check-in</TableHead>
              <TableHead>Check-out</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-mono-plex text-[11px] text-primary" data-testid={`reservation-booking-${r.id}`}>
                  {r.booking_ref || <span className="text-muted-foreground italic">—</span>}
                </TableCell>
                <TableCell>
                  <p className="font-medium">{r.guest?.name}</p>
                  <p className="text-xs text-muted-foreground">{r.guest?.phone}</p>
                  <GuestContactEdit guest={r.guest} onSaved={load} testIdPrefix={`reservation-contact-${r.id}`} />
                  {r.additional_guests && r.additional_guests.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1" data-testid={`reservation-addl-cell-${r.id}`}>
                      <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground">
                        <UsersThreeIcon size={10} /> +{r.additional_guests.length}
                      </span>
                      {r.additional_guests.slice(0, 2).map((g) => (
                        <span key={g.id} className="text-[10px] rounded-full bg-muted px-1.5 py-0.5">{g.name}</span>
                      ))}
                    </div>
                  )}
                </TableCell>
                <TableCell>{r.room?.number} · {r.room?.room_type}</TableCell>
                <TableCell>{r.check_in}</TableCell>
                <TableCell>{r.check_out}</TableCell>
                <TableCell className="font-mono-plex">{inr(r.total_amount)}</TableCell>
                <TableCell>{statusBadge(r.status)}</TableCell>
                <TableCell className="text-right">
                  {r.status === "booked" && (
                    <Button size="sm" variant="outline" onClick={() => patch(r.id, { status: "checked_in" })} data-testid={RES.checkinBtn}>
                      <SignInIcon size={14} className="mr-1" /> Check-in
                    </Button>
                  )}
                  {r.status === "checked_in" && (
                    <div className="flex items-center gap-1 justify-end flex-wrap">
                      <Button size="sm" onClick={() => checkoutAndBill(r)} data-testid={`reservation-checkout-bill-${r.id}`}>
                        <ReceiptIcon size={14} className="mr-1" /> Check-out &amp; bill
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => cancelCheckin(r)} data-testid={`reservation-undo-checkin-${r.id}`} title="Undo check-in (mistake)">
                        ↺
                      </Button>
                    </div>
                  )}
                  {r.status === "checked_out" && (
                    <Button size="sm" variant="ghost" onClick={() => cancelCheckout(r)} data-testid={`reservation-undo-checkout-${r.id}`} title="Undo check-out (mistake)">
                      ↺ Undo check-out
                    </Button>
                  )}
                  {r.status === "booked" && (
                    <Button size="sm" variant="ghost" className="ml-2" onClick={() => patch(r.id, { status: "cancelled" })} data-testid={RES.cancelBtn}>
                      <XCircleIcon size={14} />
                    </Button>
                  )}
                  {r.status !== "cancelled" && <div className="mt-2"><StayActions res={r} onChanged={load} testIdPrefix={`reservation-${r.id}`} /></div>}
                  {isMaster && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="ml-2 text-destructive hover:bg-destructive hover:text-destructive-foreground"
                      onClick={() => removeReservation(r)}
                      title="Delete reservation (master owner only)"
                      data-testid={`reservation-delete-${r.id}`}
                    >
                      <TrashIcon size={14} />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">No reservations yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

/**
 * Bulk-book multiple rooms for one guest / date range in a single click.
 * Backed by POST /api/reservations/bulk which rolls back on any per-room
 * failure. Sits next to the "New booking" button on the Reservations page.
 */
function BulkBookDialog({ rooms, guests, activeId, onCreated, onGuestsRefresh }) {
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ guest_id: "", check_in: today, check_out: "", adults: 1, children: 0, channel: "direct", rate_override: "", plan_code: "EP", notes: "", room_ids: [] });
  const [saving, setSaving] = useState(false);

  const [freeIds, setFreeIds] = useState(null);
  useEffect(() => {
    if (!open || !activeId || !form.check_in || !form.check_out || form.check_out <= form.check_in) {
      setFreeIds(null);
      setForm((f) => (f.room_ids.length ? { ...f, room_ids: [] } : f));
      return;
    }
    let live = true;
    api.get("/rooms/availability", { params: { property_id: activeId, check_in: form.check_in, check_out: form.check_out } })
      .then((r) => {
        if (!live) return;
        const ids = new Set(r.data.available_room_ids);
        setFreeIds(ids);
        setForm((f) => ({ ...f, room_ids: f.room_ids.filter((x) => ids.has(x)) }));
      })
      .catch(() => live && setFreeIds(new Set()));
    return () => { live = false; };
  }, [open, activeId, form.check_in, form.check_out]);
  const availableRooms = freeIds ? (rooms || []).filter((r) => freeIds.has(r.id)) : [];

  const toggleRoom = (id) => setForm((f) => ({ ...f, room_ids: f.room_ids.includes(id) ? f.room_ids.filter((x) => x !== id) : [...f.room_ids, id] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.guest_id) return toast.error("Pick a guest");
    if (!form.room_ids.length) return toast.error("Select at least one room");
    if (!form.check_in || !form.check_out) return toast.error("Pick check-in / check-out");
    setSaving(true);
    try {
      const payload = {
        property_id: activeId,
        guest_id: form.guest_id,
        check_in: form.check_in,
        check_out: form.check_out,
        room_ids: form.room_ids,
        adults: Number(form.adults) || 1,
        children: Number(form.children) || 0,
        channel: form.channel,
        rate_override: form.rate_override ? Number(form.rate_override) : null,
        plan_code: form.plan_code,
        notes: form.notes,
      };
      const r = await api.post("/reservations/bulk", payload);
      toast.success(`Booked ${r.data.length} room(s)`);
      setOpen(false);
      setForm({ guest_id: "", check_in: today, check_out: "", adults: 1, children: 0, channel: "direct", rate_override: "", plan_code: "EP", notes: "", room_ids: [] });
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk booking failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="reservation-bulk-btn"><StackIcon size={16} className="mr-1" />Bulk book</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto max-w-2xl" data-testid="reservation-bulk-dialog">
        <DialogHeader><DialogTitle>Bulk room booking</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Guest / group organiser</Label>
            <GuestPicker guests={guests} value={form.guest_id} onChange={(id) => setForm({ ...form, guest_id: id })} onGuestCreated={onGuestsRefresh} testIdPrefix="reservation-bulk-guest" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Check-in</Label><Input type="date" min={today} value={form.check_in} onChange={(e) => setForm({ ...form, check_in: e.target.value })} data-testid="reservation-bulk-checkin" required /></div>
            <div><Label>Check-out</Label><Input type="date" min={form.check_in || today} value={form.check_out} onChange={(e) => setForm({ ...form, check_out: e.target.value })} data-testid="reservation-bulk-checkout" required /></div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div><Label>Adults / room</Label><Input type="number" min={1} value={form.adults} onChange={(e) => setForm({ ...form, adults: e.target.value })} data-testid="reservation-bulk-adults" /></div>
            <div><Label>Children / room</Label><Input type="number" min={0} value={form.children} onChange={(e) => setForm({ ...form, children: e.target.value })} data-testid="reservation-bulk-children" /></div>
            <div>
              <Label>Channel</Label>
              <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                <SelectTrigger data-testid="reservation-bulk-channel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["direct", "booking_com", "mmt", "yatra", "agoda", "airbnb", "corporate", "public_widget"].map((c) => (
                    <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Rate override / room (₹, optional)</Label><Input type="number" value={form.rate_override} onChange={(e) => setForm({ ...form, rate_override: e.target.value })} placeholder="Blank = use each room's rate" data-testid="reservation-bulk-rate" /></div>
            <div>
              <Label>Meal plan</Label>
              <Select value={form.plan_code} onValueChange={(v) => setForm({ ...form, plan_code: v })}>
                <SelectTrigger data-testid="reservation-bulk-plan"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["EP", "CP", "MAP", "AP"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Rooms to book</Label>
              <span className="text-xs text-muted-foreground">{form.room_ids.length} selected · {availableRooms.length} available</span>
            </div>
            <div className="max-h-56 overflow-y-auto rounded-md border border-border p-2 space-y-1" data-testid="reservation-bulk-rooms">
              {availableRooms.map((r) => (
                <label key={r.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-muted/50 cursor-pointer text-sm" data-testid={`reservation-bulk-room-${r.number}`}>
                  <input type="checkbox" checked={form.room_ids.includes(r.id)} onChange={() => toggleRoom(r.id)} className="accent-primary" />
                  <span className="font-medium">{r.number}</span>
                  <span className="text-muted-foreground">· {r.room_type}</span>
                  <span className="ml-auto font-mono-plex text-xs">₹{r.rate_per_night}</span>
                </label>
              ))}
              {availableRooms.length === 0 && <p className="text-xs text-muted-foreground italic p-2" data-testid="reservation-bulk-rooms-empty">{freeIds ? "No rooms free for these dates." : "Pick check-in and check-out to see free rooms."}</p>}
            </div>
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Group name, contact person, arrival flight, etc." data-testid="reservation-bulk-notes" />
          </div>
          <DialogFooter><Button type="submit" disabled={saving} data-testid="reservation-bulk-submit">{saving ? "Booking…" : `Book ${form.room_ids.length} room(s)`}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

